import React, { useState, useEffect, useMemo } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Grid, Typography, Box, TablePagination } from '@mui/material';
import { hexToRgba } from "../../Brackets/bracketUtils/HexToRgba";
import CloseIcon from '@mui/icons-material/Close';
import './SuperPickemLeaderboard.scss';
import { amITied } from "../utils/sharedUtils";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { getTeamLogo } from "../../../utils/SportsData";
import { useUserAndGameContext } from "../../../contexts/UserAndGameContext";
import { useSuperPickemContext } from "../context/SuperPickemContext";
import { pick } from "lodash";

const SuperPickemOverallLeaderboard = ({
    myLeaderboardInfo,
    hasBracketDeadlinePassed,
    teams,
    leaderboard,
    maxPossiblePoints,
    myUserId,
    handleUsernameClick,
    hostFilter,
    hasFinalStarted,
    isGameOver,
    entriesInThisPool,
    findUserEntry,
    findChampId,
    findChampResult,
    league,
    hostIds,
    hostBackgroundColor,
    selectedTab,
    pickingTotals,
}) => {
    const {
        bracketState,
    } = useUserAndGameContext();

    const {
        lastRound,
    } = useSuperPickemContext();

    //console.log("Selected Tab", selectedTab);
    const [sortConfig, setSortConfig] = useState(() => {
        // Set the initial sort key based on the selected tab
        const getInitialSortKey = () => {
            switch(selectedTab) {
                case 'overall': return 'overallRank';
                case 'bracket': return 'bracketRank';
                case 'picks': return 'dailyPicksRank';
                default: return 'overallRank';
            }
        };
        
        return {
            key: getInitialSortKey(),
            direction: 'asc'
        };
    });
    
    // Change 2: Add a useEffect to update sort config when the tab changes
    useEffect(() => {
        // Update sort config when tab changes
        const getTabSortKey = () => {
            switch(selectedTab) {
                case 'overall': return 'overallRank';
                case 'bracket': return 'bracketRank';
                case 'picks': return 'dailyPicksRank';
                default: return 'overallRank';
            }
        };
        
        setSortConfig({
            key: getTabSortKey(),
            direction: 'asc'
        });
        
        // Reset page to 0 when tab changes
        setPage(0);
    }, [selectedTab]);
    // Pagination State
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);

    // Pagination Handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Use Effect to rest page to 0 if host filter changes
    useEffect(() => {
        //console.log("Resetting page to 0");
        setPage(0);
    }, [hostFilter, leaderboard, rowsPerPage]);

    const getRank = (entry) => {
        if (selectedTab === 'overall') {
            return entry?.overallRank || '-';
        } else if (selectedTab === 'bracket') {
            return entry?.bracketRank || '-';
        } else if (selectedTab === 'picks') {
            return entry?.dailyPicksRank || '-';
        }
        return '-';
    };
    

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const sortedData = useMemo(() => {
        if (!leaderboard) return [];
    
        return [...leaderboard].sort((a, b) => {
            const rankColumns = ['overallRank', 'bracketRank', 'dailyPicksRank'];
            const pointsColumns = ['totalPoints', 'bracketPoints', 'dailyPicksPoints', 'maxBracketPoints', 'spreadWins', 'overUnderWins'];
    
            const key = sortConfig.key; // Current column being sorted
            const direction = sortConfig.direction; // Sort direction: 'asc' or 'desc'
    
            // Rank Columns Logic
            if (rankColumns.includes(key)) {
                const aValue = a[key];
                const bValue = b[key];
                const aIsMissing = aValue == null; // Check for null or undefined
                const bIsMissing = bValue == null;
    
                // Both missing ranks should be treated equally
                if (aIsMissing && bIsMissing) return 0;
                
                // Missing ranks should always go to the bottom regardless of sort direction
                if (aIsMissing) return 1;
                if (bIsMissing) return -1;
                
                // Normal numeric sorting based on direction
                return direction === 'asc' ? aValue - bValue : bValue - aValue;
            }
    
            // Points Columns Logic
            if (pointsColumns.includes(key)) {
                const aValue = a[key];
                const bValue = b[key];
                const aIsMissing = aValue == null; // Check for null or undefined
                const bIsMissing = bValue == null;
    
                if (aIsMissing && bIsMissing) return 0; // Both missing are equal
                
                // For points, missing values should go to the bottom for both ascending and descending
                if (aIsMissing) return 1;
                if (bIsMissing) return -1;
                
                // Normal numeric sorting based on direction
                return direction === 'asc' ? aValue - bValue : bValue - aValue;
            }
    
            // Fallback for String Columns
            const aValue = a[key];
            const bValue = b[key];
            const aIsMissing = aValue == null || aValue === ''; // Missing or empty
            const bIsMissing = bValue == null || bValue === '';
    
            if (aIsMissing && bIsMissing) return 0; // Both missing are equal
            if (aIsMissing) return 1; // Missing values go to bottom
            if (bIsMissing) return -1;
            
            return direction === 'asc'
                ? (aValue || '').localeCompare(bValue || '')
                : (bValue || '').localeCompare(aValue || '');
        });
    }, [leaderboard, sortConfig]);
    

    const handleSort = (key) => {
        setPage(0); // Reset page to 0 when sorting
        setSortConfig(prevConfig => {
            // If sorting by the same column, toggle direction
            if (prevConfig.key === key) {
                return {
                    key,
                    direction: prevConfig.direction === 'asc' ? 'desc' : 'asc'
                };
            }
            
            // For rank columns, default to ascending
            if (key === 'overallRank' || key === 'bracketRank' || key === 'dailyPicksRank') {
                return {
                    key,
                    direction: 'asc'
                };
            }
    
            // For other columns, default to descending (higher values first)
            return {
                key,
                direction: 'desc'
            };
        });
    };

    const findChampColor = (result) => {
        if (result === 'win') {
            return '#00AA72';
        } else if (result === 'loss') {
            return '#CC293C';
        } else {
            return '#002129';
        }
    }

    const findChampBackgroundColor = (result) => {
        if (result === 'win') {
            return hexToRgba('#00AA72', 0.2);
        } else if (result === 'loss') {
            return hexToRgba('#CC293C', 0.2);
        } else {
            return 'transparent';
        }
    }

    const rankHeaderColor = sortConfig.key === 'overallRank' ? '#FFC60A' : '#FFFFFF';
    const totalPointsHeaderColor = sortConfig.key === 'totalPoints' ? '#FFC60A' : '#FFFFFF';
    const bracketPointsHeaderColor = sortConfig.key === 'bracketPoints' ? '#FFC60A' : '#FFFFFF';
    const pickEmPointsHeaderColor = sortConfig.key === 'dailyPicksPoints' ? '#FFC60A' : '#FFFFFF';
    const playerHeaderColor = sortConfig.key === 'username' ? '#FFC60A' : '#FFFFFF';
    

    // Slice the sorted data for pagination
    const paginatedData = sortedData?.slice(
        page * rowsPerPage, 
        page * rowsPerPage + rowsPerPage
    );

    return (
        <>
            {hasBracketDeadlinePassed && (
                <Box>
                    <Typography
                        sx={{
                            color: '#002129',
                            fontSize: isMobile ? '12px' : '16px',
                            fontWeight: '700',
                            textAlign: 'center',
                            marginBottom: '5px',
                        }}
                    >
                        To view a user's bracket, click on their username.
                    </Typography>
                </Box>
            )}

            <Grid item xs={12} sx={{overflow: 'scroll'}}>
                <Box
                    sx={{
                        maxWidth: '900px',
                        margin: '0 auto',
                    }}
                >
                    <TablePagination
                        rowsPerPageOptions={[50, 100]}
                        component="div"
                        count={sortedData?.length || 0}
                        rowsPerPage={rowsPerPage}
                        page={Math.min(page, Math.max(0, Math.ceil((leaderboard?.length || 0) / rowsPerPage) - 1))} // Derive a valid page value
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Rows"
                        sx={{
                            backgroundColor: 'white',
                            '& .MuiTablePagination-toolbar': {
                                color: '#002129',
                            },
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input, & .MuiTablePagination-actions': {
                                color: '#002129',
                            }
                        }}
                    />
                </Box>
                <TableContainer component={Paper}>
                    <Table className="super-pickem-leaderboard-table">
                        <TableHead 
                            sx={{
                                background: '#002129',
                                color:'#fff'
                            }}>

                                {myLeaderboardInfo?.myInfo?.length > 0 && (
                                    <>
                                        {myLeaderboardInfo?.myInfo?.map((info, infoIndex) => {
                                            const usersChamp = bracketState?.find(pick => pick?.round === lastRound);
                                            const championTeam = usersChamp?.teamId;
                                            const championResult = usersChamp?.result;

                                            return (
                                                <TableRow 
                                                key={`my-info-${infoIndex}`} 
                                                sx={{ background: '#F0F1F1', color: '#002129' }}
                                            >
                                                <TableCell 
                                                    sx={{ 
                                                        textAlign: 'center', 
                                                        fontSize: '12px',
                                                        position: 'sticky',
                                                        left: 0,
                                                        zIndex: 3,
                                                        backgroundColor: '#F0F1F1',
                                                    }}
                                                >
                                                    <Typography sx={{ fontSize: '12px', color: '#002129' }}>
                                                        {getRank(info)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell 
                                                    sx={{
                                                        textAlign:'left', 
                                                        fontSize:'12px',
                                                        position: 'sticky',
                                                        left: '75px',
                                                        zIndex: 3,
                                                        backgroundColor: '#F0F1F1',
                                                        textDecoration: hasBracketDeadlinePassed ? 'underline' : 'none',
                                                        cursor: hasBracketDeadlinePassed ? 'pointer' : 'default',
                                                        fontWeight: hasBracketDeadlinePassed ? '700' : '400',
                                                        width: { xs: '160px', sm: '175px', md: '40%' }, // Fixed width for mobile
                                                        maxWidth: { xs: '160px', sm: '175px', md: '40%' }, // Fixed max-width for mobile
                                                        minWidth: { xs: '160px', sm: '175px' }, // Add a minimum width
                                                    }}
                                                    onClick={() => hasBracketDeadlinePassed ? handleUsernameClick(info?.username) : null}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column', // Stack rows vertically
                                                            alignItems: 'flex-start', // Align rows to the left
                                                            width: '100%', // Take up full width of parent
                                                        }}
                                                    >
                                                        {/* Row 1: Username and Bar Logo */}
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                width: '100%', // Take full width of parent
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontSize: '12px',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    maxWidth: 'calc(100% - 35px)', // Reserve space for logo
                                                                    textDecoration: hasBracketDeadlinePassed ? 'underline' : 'none',
                                                                    fontWeight: hasBracketDeadlinePassed ? '700' : '400',
                                                                }}
                                                            >
                                                                {info?.username}
                                                            </Typography>
                                                            {info?.logo && (
                                                                <img
                                                                    src={info.logo}
                                                                    alt="User Logo"
                                                                    style={{ 
                                                                        maxHeight: '30px',
                                                                        minWidth: '30px',
                                                                        marginLeft: '5px',
                                                                        flexShrink: 0 // Prevent the image from shrinking
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>

                                                        {/* Row 2: Tiebreaker Points */}
                                                        {hasFinalStarted && (
                                                            <Typography
                                                                sx={{
                                                                    fontSize: '10px', // Adjust font size for this row
                                                                    fontWeight: 500,
                                                                    color: '#002129',
                                                                    textDecoration: 'none',
                                                                }}
                                                            >
                                                                Tiebreaker Pts: {info?.tiebreaker?.[0]?.points ?? 0}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>

                                                {selectedTab !== 'picks' && (
                                                    <TableCell 
                                                        sx={{ 
                                                            textAlign: 'center', 
                                                            fontSize: '12px', 
                                                            width: { md: "5%", lg: "5%" } 
                                                        }}
                                                    >
                                                        {championTeam ? (
                                                            <img
                                                                className="super-pickem-leaderboard-champ-image"
                                                                style={{
                                                                    border: `1px solid ${findChampColor(championResult)}`,
                                                                    backgroundColor: findChampBackgroundColor(championResult),
                                                                }}
                                                                src={getTeamLogo(league, championTeam, teams)}
                                                                alt="TEAM LOGO"
                                                                loading='lazy'
                                                            />
                                                        ) : !hasBracketDeadlinePassed ? (
                                                            <Box
                                                                className="super-pickem-leaderboard-champ-placeholder"
                                                            />
                                                        ) : (
                                                            <Box
                                                                className="super-pickem-leader-no-champ"
                                                            >
                                                                <CloseIcon sx={{ color: '#CC293C', fontSize: '16px' }} />
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                )}
                                                
                                                {selectedTab === 'overall' && (
                                                    <TableCell sx={{ textAlign: 'center', fontSize: '12px', width:{md:"20%", lg:"20%"} }}>
                                                        {info?.totalPoints || 0}
                                                    </TableCell>
                                                )}

                                                {selectedTab !== 'picks' && (
                                                    <TableCell sx={{ textAlign: 'center', fontSize: '12px', width:{md:"20%", lg:"20%"} }}>
                                                        {info?.bracketPoints || 0}
                                                    </TableCell>
                                                )}
                                                
                                                {selectedTab !== 'bracket' && (
                                                    <TableCell sx={{ textAlign: 'center', fontSize: '12px', width:{md:"20%", lg:"20%"} }}>
                                                        {info?.dailyPicksPoints || 0}
                                                    </TableCell>
                                                )}

                                                {selectedTab === 'bracket' && (
                                                    <TableCell sx={{ textAlign: 'center', fontSize: '12px', width:{md:"20%", lg:"20%"} }}>
                                                        {hasBracketDeadlinePassed ? info?.maxPossiblePoints ? info?.maxPossiblePoints : 0 : maxPossiblePoints}
                                                    </TableCell>
                                                )}

                                                {selectedTab === 'picks' && (
                                                    <TableCell sx={{ textAlign: 'center', fontSize: '12px', width:{md:"20%", lg:"20%"} }}>
                                                        {info?.spreadWins || 0} - {info?.spreadLosses || 0} - {info?.spreadPushes || 0}
                                                    </TableCell>
                                                )}

                                                {selectedTab === 'picks' && pickingTotals && (
                                                    <TableCell sx={{ textAlign: 'center', fontSize: '12px', width:{md:"20%", lg:"20%"} }}>
                                                        {info?.overUnderWins || 0} - {info?.overUnderLosses || 0} - {info?.overUnderPushes || 0}
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        );
                                    })}
                                    </>
                                )
                            }

                                <TableRow 
                                    sx={{
                                        color:'#fff'
                                    }}>
                                    <TableCell
                                        onClick={() => handleSort('overallRank')}
                                        sx={{
                                            color: rankHeaderColor,
                                            fontWeight: 900,
                                            textAlign: 'center',
                                            // width: "30px",
                                            textDecoration: 'underline',
                                            position: 'sticky',
                                            backgroundColor: '#002129',
                                            zIndex: 3,
                                            left: 0,
                                        }}
                                    >
                                        RANK
                                    </TableCell>

                                    <TableCell 
                                        onClick={() => handleSort('username')}
                                        sx={{ 
                                            color: playerHeaderColor, 
                                            fontWeight: 900, 
                                            textAlign: 'left',
                                            textDecoration: 'underline',
                                            position: 'sticky',
                                            backgroundColor: '#002129',
                                            zIndex: 3,
                                            left: '74px', // Adjust based on the width of the previous sticky cell
                                        }}
                                    >
                                        PLAYER
                                    </TableCell>

                                    {selectedTab !== 'picks' && (
                                        <TableCell sx={{ color: '#fff', fontWeight: 900, textAlign: 'center'}} >CHAMP</TableCell>
                                    )}
                                    
                                    {selectedTab === 'overall' && (
                                        <TableCell
                                            onClick={() => handleSort('totalPoints')}
                                            sx={{
                                                color: totalPointsHeaderColor,
                                                fontWeight: 900,
                                                textAlign: 'center',
                                                // width: '97px',
                                                position: 'relative',
                                                textDecoration: 'underline',
                                            }}
                                        >
                                            TOT PTS
                                        </TableCell>
                                    )}

                                    {selectedTab !== 'picks' && (
                                        <TableCell
                                            onClick={() => handleSort('bracketPoints')}
                                            sx={{
                                                color: bracketPointsHeaderColor,
                                                fontWeight: 900,
                                                textAlign: 'center',
                                                position: 'relative',
                                                textDecoration: 'underline',
                                            }}
                                        >
                                            BRACKET PTS
                                        </TableCell>
                                    )}

                                    {selectedTab !== 'bracket' && (
                                        <TableCell
                                            onClick={() => handleSort('dailyPicksPoints')} 
                                            sx={{ 
                                                color: pickEmPointsHeaderColor, 
                                                fontWeight: 900, 
                                                textAlign: 'center', 
                                                position: 'relative',
                                                textDecoration: 'underline',
                                                minWidth: '100px',
                                            }}
                                        >
                                            PICK 'EM PTS
                                        </TableCell>
                                    )}

                                    {selectedTab === 'bracket' && (
                                        <TableCell
                                            onClick={() => handleSort('maxPossiblePoints')} 
                                            sx={{ 
                                                color: pickEmPointsHeaderColor, 
                                                fontWeight: 900, 
                                                textAlign: 'center', 
                                                position: 'relative',
                                                textDecoration: 'underline',
                                                minWidth: '100px',
                                            }}
                                        >
                                            MAX
                                        </TableCell>
                                    )}

                                    {selectedTab === 'picks' && (
                                        <TableCell
                                            onClick={() => handleSort('atsRecord')} 
                                            sx={{ 
                                                color: pickEmPointsHeaderColor, 
                                                fontWeight: 900, 
                                                textAlign: 'center', 
                                                position: 'relative',
                                                textDecoration: 'underline',
                                                minWidth: '100px',
                                            }}
                                        >
                                            ATS RECORD
                                        </TableCell>
                                    )}

                                    {selectedTab === 'picks' && pickingTotals && (
                                        <TableCell
                                            onClick={() => handleSort('overUnderRecord')} 
                                            sx={{ 
                                                color: pickEmPointsHeaderColor, 
                                                fontWeight: 900, 
                                                textAlign: 'center', 
                                                position: 'relative',
                                                textDecoration: 'underline',
                                                minWidth: '100px',
                                            }}
                                        >
                                            O/U RECORD
                                        </TableCell>
                                    )}

                                </TableRow>
                            </TableHead>
                        <TableBody>
                            {paginatedData?.map((row, index) => (
                                <TableRow
                                    key={index}
                                    sx={{
                                        backgroundColor: 
                                            hostIds?.includes(row?.userId) 
                                                ? hostBackgroundColor 
                                                : row?.userId === myUserId 
                                                    ? '#FFF6DA' 
                                                    : index % 2 === 0 
                                                        ? '#FFF' 
                                                        : '#F0F1F1',
                                    }}
                                >                        
                                    <TableCell 
                                        sx={{ 
                                            textAlign: 'center', 
                                            fontSize: '12px',
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 3,
                                            backgroundColor: hostIds?.includes(row?.userId) ? hostBackgroundColor : row?.userId === myUserId ? '#FFF6DA' : index % 2 === 0 ? '#FFF' : '#F0F1F1',
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '12px', fontWeight: '500' }}>
                                            {getRank(row)}
                                        </Typography>
                                    </TableCell>
                                    
                                    <TableCell 
                                        sx={{
                                            textAlign:'left', 
                                            fontSize:'12px',
                                            position: 'sticky',
                                            left: '75px',
                                            zIndex: 3,
                                            backgroundColor: hostIds?.includes(row?.userId) ? hostBackgroundColor : row?.userId === myUserId ? '#FFF6DA' : index % 2 === 0 ? '#FFF' : '#F0F1F1',
                                            textDecoration: hasBracketDeadlinePassed ? 'underline' : 'none',
                                            cursor: hasBracketDeadlinePassed ? 'pointer' : 'default',
                                            fontWeight: hasBracketDeadlinePassed ? '700' : '500',
                                        }}
                                        onClick={() => hasBracketDeadlinePassed ? handleUsernameClick(row?.username, 'bracket') : null}
                                    >
                                         <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    {row?.username}
                                                    {row?.logo && (
                                                        <img
                                                            src={row.logo}
                                                            alt="User Logo"
                                                            style={{ 
                                                                maxHeight: '30px',
                                                                minWidth: '30px',
                                                                marginLeft: '5px',
                                                                flexShrink: 0 // Prevent the image from shrinking
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                                {hasFinalStarted && (
                                                    <Typography
                                                        sx={{
                                                            fontSize: '10px',
                                                            fontWeight: 500,
                                                            color: '#002129',
                                                            textDecoration: 'none',
                                                        }}
                                                    >
                                                        Tiebreaker Pts: {row?.tiebreaker?.[0]?.points ?? 0}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    
                                    {selectedTab !== 'picks' && (
                                        <TableCell 
                                            sx={{ 
                                                textAlign: 'center', 
                                                fontSize: '12px', 
                                            }}
                                        >
                                            {/* For the current user, always show their champion from bracketState if available */}
                                            {row?.userId === myUserId && bracketState ? (
                                                (() => {
                                                    // Get user's champion from bracketState for current user
                                                    const usersChamp = bracketState?.find(pick => pick?.round === lastRound);
                                                    if (usersChamp && usersChamp?.teamId) {
                                                        return (
                                                            <img
                                                                className="super-pickem-leaderboard-champ-image"
                                                                style={{
                                                                    border: `1px solid ${findChampColor(usersChamp?.result)}`,
                                                                    backgroundColor: findChampBackgroundColor(usersChamp?.result),
                                                                }}
                                                                src={getTeamLogo(league, usersChamp?.teamId, teams)}
                                                                alt="TEAM LOGO"
                                                            />
                                                        );
                                                    } else if (!hasBracketDeadlinePassed) {
                                                        return (
                                                            <Box
                                                                className="super-pickem-leaderboard-champ-placeholder"
                                                            />
                                                        );
                                                    } else {
                                                        return (
                                                            <Box
                                                                className="super-pickem-leader-no-champ"
                                                            >
                                                                <CloseIcon sx={{ color: '#CC293C', fontSize: '16px' }} />
                                                            </Box>
                                                        );
                                                    }
                                                })()
                                            ) : (
                                                // For other users, use the existing logic
                                                (!hasBracketDeadlinePassed && (!row?.champ || row?.userId !== myUserId)) ? (
                                                    <Box
                                                        className="super-pickem-leaderboard-champ-placeholder"
                                                    />
                                                ) : row?.champ ? (
                                                    <img
                                                        className="super-pickem-leaderboard-champ-image"
                                                        style={{
                                                            border: `1px solid ${findChampColor(row?.champResult)}`,
                                                            backgroundColor: findChampBackgroundColor(row?.champResult),
                                                        }}
                                                        src={getTeamLogo(league, row?.champ, teams)}
                                                        alt="TEAM LOGO"
                                                    />
                                                ) : (
                                                    <Box
                                                        className="super-pickem-leader-no-champ"
                                                    >
                                                        <CloseIcon sx={{ color: '#CC293C', fontSize: '16px' }} />
                                                    </Box>
                                                )
                                            )}
                                        </TableCell>
                                    )}
                                    
                                    {selectedTab === 'overall' && (
                                        <TableCell sx={{textAlign:'center', fontSize:'12px',}}>{row?.totalPoints || 0}</TableCell>
                                    )}

                                    {selectedTab !== 'picks' && (
                                        <TableCell sx={{ textAlign: 'center', fontSize: '12px' }}>
                                            {row?.bracketPoints || 0}
                                        </TableCell>
                                    )}

                                    {selectedTab !== 'bracket' && (
                                        <TableCell sx={{ textAlign: 'center', fontSize: '12px' }}>
                                            {row?.dailyPicksPoints || 0}
                                        </TableCell>
                                    )}

                                    {selectedTab === 'bracket' && (
                                        <TableCell sx={{ textAlign: 'center', fontSize: '12px' }}>
                                            {hasBracketDeadlinePassed ? row?.maxPossiblePoints ? row?.maxPossiblePoints : 0 : maxPossiblePoints}
                                        </TableCell>
                                    )}

                                    {selectedTab === 'picks' && (
                                        <TableCell sx={{ textAlign: 'center', fontSize: '12px' }}>
                                            {row?.spreadWins || 0} - {row?.spreadLosses || 0} - {row?.spreadPushes || 0}
                                        </TableCell>
                                    )}

                                    {selectedTab === 'picks' && pickingTotals && (
                                        <TableCell sx={{ textAlign: 'center', fontSize: '12px' }}>
                                            {row?.overUnderWins || 0} - {row?.overUnderLosses || 0} - {row?.overUnderPushes || 0}
                                        </TableCell>
                                    )}


                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box
                        sx={{
                            maxWidth: '900px',
                            margin: '0 auto',
                        }}
                    >
                        <TablePagination
                            rowsPerPageOptions={[50, 100]}
                            component="div"
                            count={sortedData?.length || 0}
                            rowsPerPage={rowsPerPage}
                            page={Math.min(page, Math.max(0, Math.ceil((leaderboard?.length || 0) / rowsPerPage) - 1))} // Derive a valid page value
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Rows"
                            sx={{
                                backgroundColor: 'white',
                                '& .MuiTablePagination-toolbar': {
                                    color: '#002129',
                                },
                                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input, & .MuiTablePagination-actions': {
                                    color: '#002129',
                                }
                            }}
                        />
                    </Box>
            </Grid>
        </>

    )
}

export default SuperPickemOverallLeaderboard;